interface LottieLayerWorldTransform {
	x: number;
	y: number;
	rotation: number;
}

interface LottieTextureFrame {
	baseX: number;
	baseY: number;
	width: number;
	height: number;
	originX: number;
	originY: number;
}

export interface LottieLayerTextureTransform {
	x: number;
	y: number;
	rotation: number;
}

export function getLottieLayerTextureTransform({
	animationData,
	layerName,
	frame,
	textureFrame,
}: {
	animationData: any;
	layerName: string;
	frame: number;
	textureFrame: LottieTextureFrame;
}): LottieLayerTextureTransform {
	const layer = animationData.layers?.find((candidate: any) => candidate.nm === layerName);

	if (!layer) {
		throw new Error(`Missing Lottie layer: ${layerName}`);
	}

	const layerAnchor = getLottieVectorValue(layer.ks.a, frame);
	const layerTransform = getLottieLayerWorldTransform(animationData, layer, layerAnchor, frame);
	const textureTransform = mapLottiePointToTexture(textureFrame, animationData, layerTransform.x, layerTransform.y);

	return {
		x: textureTransform.x,
		y: textureTransform.y,
		rotation: layerTransform.rotation,
	};
}

export function getLottieMotionOffset({
	currentFrame,
	totalFrames,
	motionAmplitude,
	motionPhase,
}: {
	currentFrame: number;
	totalFrames: number;
	motionAmplitude: number;
	motionPhase: number;
}) {
	if (!motionAmplitude || !totalFrames) {
		return 0;
	}

	const progress = currentFrame / totalFrames;

	return Math.sin(progress * Math.PI * 2 + motionPhase) * motionAmplitude;
}

function getLottieLayerWorldTransform(
	animationData: any,
	layer: any,
	point: number[],
	frame: number,
): LottieLayerWorldTransform {
	const position = getLottieVectorValue(layer.ks.p, frame);
	const anchor = getLottieVectorValue(layer.ks.a, frame);
	const scale = getLottieVectorValue(layer.ks.s, frame);
	const rotation = getLottieNumberValue(layer.ks.r, frame);
	const transformedPoint = transformLottiePoint(point, position, anchor, scale, rotation);

	if (!layer.parent) {
		return {
			...transformedPoint,
			rotation,
		};
	}

	const parentLayer = animationData.layers?.find((candidate: any) => candidate.ind === layer.parent);

	if (!parentLayer) {
		throw new Error(`Missing Lottie parent layer: ${layer.parent}`);
	}

	const parentTransform: LottieLayerWorldTransform = getLottieLayerWorldTransform(
		animationData,
		parentLayer,
		[transformedPoint.x, transformedPoint.y, 0],
		frame,
	);

	return {
		...parentTransform,
		rotation: parentTransform.rotation + rotation,
	};
}

function transformLottiePoint(
	point: number[],
	position: number[],
	anchor: number[],
	scale: number[],
	rotation: number,
) {
	const radians = (rotation * Math.PI) / 180;
	const scaleX = (scale[0] ?? 100) / 100;
	const scaleY = (scale[1] ?? 100) / 100;
	const x = (point[0] - anchor[0]) * scaleX;
	const y = (point[1] - anchor[1]) * scaleY;

	return {
		x: position[0] + x * Math.cos(radians) - y * Math.sin(radians),
		y: position[1] + x * Math.sin(radians) + y * Math.cos(radians),
	};
}

function mapLottiePointToTexture(textureFrame: LottieTextureFrame, animationData: any, x: number, y: number) {
	const scale = Math.min(textureFrame.width / animationData.w, textureFrame.height / animationData.h);
	const renderedWidth = animationData.w * scale;
	const renderedHeight = animationData.h * scale;
	const paddingX = (textureFrame.width - renderedWidth) / 2;
	const paddingY = (textureFrame.height - renderedHeight) / 2;

	return {
		x: textureFrame.baseX + paddingX + x * scale - textureFrame.width * textureFrame.originX,
		y: textureFrame.baseY + paddingY + y * scale - textureFrame.height * textureFrame.originY,
	};
}

function getLottieVectorValue(property: any, frame: number) {
	const value = getLottiePropertyValue(property, frame);

	return Array.isArray(value) ? value : [value, value, value];
}

function getLottieNumberValue(property: any, frame: number) {
	const value = getLottiePropertyValue(property, frame);

	return Array.isArray(value) ? value[0] : value;
}

function getLottiePropertyValue(property: any, frame: number) {
	if (!property.a) {
		return property.k;
	}

	const keyframes = property.k;
	const nextKeyframeIndex = keyframes.findIndex((keyframe: any) => keyframe.t > frame);

	if (nextKeyframeIndex === -1) {
		return keyframes[keyframes.length - 1].s;
	}

	if (nextKeyframeIndex === 0) {
		return keyframes[0].s;
	}

	const keyframe = keyframes[nextKeyframeIndex - 1];
	const nextKeyframe = keyframes[nextKeyframeIndex];
	const progress = getLottieKeyframeProgress(keyframe, nextKeyframe, frame);
	const startValue = keyframe.s;
	const endValue = keyframe.e ?? nextKeyframe.s;

	if (Array.isArray(startValue)) {
		return startValue.map((start: number, index: number) => {
			return getLinearValue(start, endValue[index], progress);
		});
	}

	return getLinearValue(startValue, endValue, progress);
}

function getLottieKeyframeProgress(keyframe: any, nextKeyframe: any, frame: number) {
	const rawProgress = clamp((frame - keyframe.t) / (nextKeyframe.t - keyframe.t), 0, 1);
	const outX = Array.isArray(keyframe.o?.x) ? keyframe.o.x[0] : keyframe.o?.x;
	const outY = Array.isArray(keyframe.o?.y) ? keyframe.o.y[0] : keyframe.o?.y;
	const inX = Array.isArray(keyframe.i?.x) ? keyframe.i.x[0] : keyframe.i?.x;
	const inY = Array.isArray(keyframe.i?.y) ? keyframe.i.y[0] : keyframe.i?.y;

	if ([outX, outY, inX, inY].some((value) => value === undefined)) {
		return rawProgress;
	}

	return getCubicBezierYForX(rawProgress, outX, outY, inX, inY);
}

function getCubicBezierYForX(x: number, x1: number, y1: number, x2: number, y2: number) {
	let lower = 0;
	let upper = 1;
	let t = x;

	for (let iteration = 0; iteration < 8; iteration++) {
		const estimate = getCubicBezierValue(t, x1, x2);

		if (Math.abs(estimate - x) < 0.001) {
			break;
		}

		if (estimate < x) {
			lower = t;
		} else {
			upper = t;
		}

		t = (lower + upper) / 2;
	}

	return getCubicBezierValue(t, y1, y2);
}

function getCubicBezierValue(t: number, value1: number, value2: number) {
	const inverseT = 1 - t;

	return 3 * inverseT * inverseT * t * value1 + 3 * inverseT * t * t * value2 + t * t * t;
}

function getLinearValue(start: number, end: number, progress: number) {
	return start + (end - start) * progress;
}

function clamp(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}
